import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Select, Upload, message, notification } from 'antd';
import type { UploadProps } from 'antd';
import { ArrowLeftOutlined, PlusCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import Locations from '@/components/Locations/Locations/Locations';
import { connect, useDispatch } from 'react-redux';
import LocationService from '../../../../domain/Location/LocationService';
import InpService from '../../../../domain/ModelFile/InpService';
import PluviometerService from '../../../../domain/Pluviometers/PluviometerService';
import { AnyAction } from 'redux';
import './index.scss';
import { InspectDatsRequest } from '@/domain/ModelFile/Inp';
import { setDataModelFile } from '@/redux/slices/modelFileSlice';
import store from '@/redux/store';

// Location node shape used by this page
interface LocationNode {
  locationId: number;
  locationName?: string;
  networkType?: string | null;
  childLocations?: LocationNode[];
}

// Helper: find a location by id within nested childLocations
function findLocationById(locationsList: LocationNode[], locationId: number): LocationNode | null {
  for (const loc of locationsList) {
    if (loc.locationId === locationId) {
      return loc;
    }
    if (Array.isArray(loc.childLocations) && loc.childLocations.length > 0) {
      const found = findLocationById(loc.childLocations, locationId);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

const locationService = new LocationService();
const pluviometerService = new PluviometerService();
const inpService = new InpService();
const { Dragger } = Upload;
type Props = {};
function UploadModelFiles() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [messageApi, contextHolder] = message.useMessage();
	const [inpFile, setInpFile] = useState<any[]>([]);
	const [icmtFile, setIcmtFile] = useState<any[]>([]);
	const [datFiles, setDatFiles] = useState<any[]>([]);
    const [locations, setlocations] = useState<any[]>([]);
	const [selectLocation, setSelectlocations] = useState<number>();
	const [pluviometers, setPluviometers] = useState<any[]>([]);
	const [selectedPluviometers, setselectedPluviometers] = useState<any[]>([]);
	const [selectedModelType, setSelectedModelType] = useState<any>('WATER');
	const [selectedEngineType, setSelectedEngineType] = useState('EPANET');
	const [isModelTypeLocked, setIsModelTypeLocked] = useState(false);
	const [requiredDatFiles, setRequiredDatFiles] = useState<string[]>([]);

	// Limit engine options by model type
	const engineOptions =
		selectedModelType === 'WATER'
			? [{ value: 'EPANET', label: 'EPANET' }]
			: [
					{ value: 'SWMM', label: 'SWMM' },
					{ value: 'ICM', label: 'ICM' },
				];

	// Accepted file types vary by engine
	const optionsUpload = () => {
		switch (selectedEngineType) {
			case 'SWMM':
				return '.inp';
			case 'ICM':
				return '.inp,.icmt';
			case 'EPANET':
				return '.inp';
			default:
				return '.inp';
		}
	};

	const initialUploadProps: UploadProps = {
		name: 'file',
		multiple: true,
		accept: optionsUpload(),
		showUploadList: false,
		beforeUpload: (file: File) => {
			const nameSplit = file.name.split('.');
			if (file.name.length > 100) {
				messageApi.open({
					type: 'error',
					content: `File name ${file.name} exceeds 100 characters`,
				});
				notification.error({
					message: 'Error',
					description: `File name ${file.name} exceeds 100 characters`,
				});
				return false;
			}

			const extension = nameSplit[nameSplit.length - 1];

			if (extension === 'icmt' && selectedEngineType === 'ICM') {
				// Add null check for inpFile before accessing [0]
				const inpFileName = inpFile && inpFile.length > 0 ? (inpFile[0]?.name ?? '') : '';
				const icmtBaseName = file.name.split('.')[0];
				const inpBaseName = inpFileName.split('.')[0];

				if (icmtBaseName !== inpBaseName) {
					openNotificationInpIcmtIquals();
					return;
				}
			}

			switch (extension) {
				case 'inp':
					setInpFile([file]);
					break;
				case 'icmt':
					setIcmtFile([file]);
					break;
				default:
					showErrorFile();
					break;
			}
			return false;
		},
	};

	const handleSelectEngineType = (val: string) => {
		setSelectedEngineType(val);
		if (val === 'SWMM') {
			setIcmtFile([]);
		}
	};

	const showErrorFile = () => {
		messageApi.open({
			type: 'error',
			content: 'One of the files selected is not valid',
		});
	};

	const deleteInp = () => {
		setInpFile([]);
		setIcmtFile([]);
		setSelectlocations(undefined);
		setPluviometers([]);
		setselectedPluviometers([]);
		setRequiredDatFiles([]);
		setSelectedModelType('WATER');
		setSelectedEngineType('EPANET');
		setIsModelTypeLocked(false);
	};
	const deleteIcmt = () => {
		setIcmtFile([]);
	};

	const deleteRequiredDat = (datName: string) => {
		setRequiredDatFiles((previousState) => previousState.filter((name) => name !== datName));
	};

	const getSelectedPluviometers = (uid: any): string[] => {
		const value = selectedPluviometers.find((pluvi) => pluvi.uid === uid)?.value;
		return value ? value.split(',') : [];
	};

	const handleSelectPluviometers = (uid: any, value: string[]) => {
		const newArray = [...selectedPluviometers];
		const findObject = newArray.find((pluvi) => pluvi.uid === uid);
		if (findObject) {
			findObject.value = value.join(',');
			setselectedPluviometers(newArray);
		} else {
			setselectedPluviometers([
				...selectedPluviometers,
				{
					uid,
					value: value.join(','),
				},
			]);
		}
	};

	const pluviometerOptions = pluviometers.map((pluv) => {
		return {
			value: pluv,
			label: pluv,
		};
	});

	const engineRequiresPluviometers = selectedEngineType === 'SWMM' || selectedEngineType === 'ICM';

	const hasPluviometerForEveryDat =
		requiredDatFiles.length > 0 &&
		requiredDatFiles.every((datName) => getSelectedPluviometers(datName).length > 0);

	const isUploadDisabled = engineRequiresPluviometers && !hasPluviometerForEveryDat;

	const blobToBase64 = async (blob: any): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(blob);
			reader.onload = () => {
				if (reader.result) {
					const result = reader.result as string;
					resolve(result.split(',')[1]);
				} else {
					resolve('');
				}
			};
			reader.onerror = (error) => reject(error);
		});
	};

	const uploadFiles = async () => {
		// Add null check for inpFile before accessing [0]
		if (inpFile && inpFile.length > 0) {
			if (!selectLocation) {
				notification.open({
					message: 'Error',
					description: 'You must select the location',
					type: 'error',
				});
				return;
			}
			let data = {} as any;
			if (inpFile[0].isMms) {
				data = {
					inpName: inpFile[0].name,
					modelId: inpFile[0].mmsId,
					modelType: selectedModelType,
					locationId: selectLocation,
					engineType: selectedEngineType,
				};
			} else {
				data = {
					inpName: inpFile[0].name,
					inpData: await blobToBase64(inpFile[0]),
					modelType: selectedModelType,
					locationId: selectLocation,
					engineType: selectedEngineType,
				};
			}

			if (selectedModelType == 'WATER') {
				const oui = JSON.parse(localStorage.getItem('security') || '').organizationId;
				const userId = localStorage.getItem('userId') || '';
				await inpService.uploadInps(userId, oui, data);
				notification.success({
					message: 'Successful',
					description: 'Files upload successfully',
				});
				navigate(`/upload?selectedTab=MODEL`);
			} else {
				if (requiredDatFiles.length === 0) {
					openNotificationNoDat();
					return;
				}

				data.engineFiles = [] as any[];
				for (let index = 0; index < requiredDatFiles.length; index++) {
					const datName = requiredDatFiles[index];
					const pluviometers =
						selectedPluviometers.find((pluv) => pluv.uid === datName)?.value || '';
					if (pluviometers.length <= 0) {
						notification.open({
							message: 'Error',
							description: 'All DAT files must have at least one rain gauge',
							type: 'error',
						});
						return;
					}
					data.engineFiles.push({
						name: datName,
						pluviometers,
					});
				}

				if (selectedEngineType === 'ICM') {
					if (icmtFile.length == 0) {
						openNotificationNoIcmt();
						return;
					}

					for (let index = 0; index < icmtFile.length; index++) {
						const files = icmtFile[index];
						data.engineFiles.push({
							name: files.name,
							data: await blobToBase64(files),
						});
					}
				}
				const oui = JSON.parse(localStorage.getItem('security') || '').organizationId;
				const userId = localStorage.getItem('userId') || '';
				await inpService.uploadInps(userId, oui, data);
				notification.success({
					message: 'Successful',
					description: 'Files upload successfully',
				});
				navigate(`/upload?selectedTab=MODEL`);
			}
		} else {
			openNotification();
		}
	};

	const openNotificationNoDat = () => {
		notification.open({
			message: 'Error',
			description: 'At least you need to upload one DAT file.',
			type: 'error',
		});
	};

	const openNotificationNoIcmt = () => {
		notification.open({
			message: 'Error',
			description: 'At least you need to upload one ICMT file.',
			type: 'error',
		});
	};

	const openNotificationInpIcmtIquals = () => {
		notification.open({
			message: 'Error',
			description: 'The names of the INP and ICMT files must be the same.',
			type: 'error',
		});
	};

	const openNotification = () => {
		notification.open({
			message: 'Error',
			description: 'You need to select a INP file.',
			type: 'error',
		});
	};

	const handleGetLocations = async () => {
		const userId = localStorage.getItem('userId') || '';
		const res = await locationService.getLocationsByUser(userId);
		setlocations(res);
	};

	const handleGetPluviometers = async () => {
		if (selectLocation) {
			const res = await pluviometerService.getPluviometersByLocation(selectLocation);
			setPluviometers(res);
		}
	};

	const buildInspectDatsRequest = async (file: any): Promise<InspectDatsRequest> => {
		const baseRequest = {
			inpName: file.name,
			modelType: selectedModelType,
			engineType: selectedEngineType,
			locationId: selectLocation as number,
		};
		return file.isMms
			? { ...baseRequest, modelId: Number(file.mmsId) }
			: { ...baseRequest, inpData: await blobToBase64(file) };
	};



	// Update model type from the selected location's networkType (optional chaining for clarity)
	const handleClickLocation = async (location: number) => {
		setSelectlocations(location);
		const selectedLoc = findLocationById(locations, location);
		const networkType = selectedLoc?.networkType;
		setIsModelTypeLocked(!!networkType);
		if (networkType) {
			const modelType = networkType === 'WATER' ? 'WATER' : 'DRAINAGE';
			setSelectedModelType(modelType);
			setSelectedEngineType(modelType === 'WATER' ? 'EPANET' : 'SWMM');
		}
	};

	const goToEdditFile = async () => {
		if (inpFile.length > 0) {
			const data = {
				inpName: inpFile[0].name,
				inpdata: await blobToBase64(inpFile[0]),
				modelType: selectedModelType,
				locationId: selectLocation,
				dat: [],
			} as any;
			if (datFiles.length > 0) {
				for (let index = 0; index < datFiles.length; index++) {
					const files = datFiles[index];
					const pluviometers = selectedPluviometers.find((pluv) => pluv.uid === files.uid)?.value;
					if (pluviometers && pluviometers.length <= 0) return;
					data.dat.push({
						datName: files.name,
						datData: await blobToBase64(files),
						pluviometers,
					});
				}
			}
			dispatch(setDataModelFile(data) as unknown as AnyAction);
			navigate(`/upload/modelfiles/editFile`);
		}
	};

	const fileSize = (size: any) => {
		if (size === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
		const i = Math.floor(Math.log(size) / Math.log(k));
		return `${(size / Math.pow(k, i)).toFixed(3)} ${sizes[i]}`;
	};

	function addExtension(inpString: any) {
		if (!inpString.endsWith('.inp')) {
			return inpString + '.inp';
		}
		return inpString;
	}

	useEffect(() => {
		handleGetLocations();
	}, []);

	useEffect(() => {
		handleGetPluviometers();
	}, [selectLocation]);

	useEffect(() => {
		setselectedPluviometers([]);

		// WATER models (EPANET) do not use DAT files, skip the inspection entirely
		if (
			selectedModelType === 'WATER' ||
			!selectLocation ||
			!selectedEngineType ||
			inpFile.length === 0
		) {
			setRequiredDatFiles([]);
			return;
		}

		let isCurrentRequest = true;
		const handleGetRequiredDatFiles = async () => {
			try {
				const request = await buildInspectDatsRequest(inpFile[0]);
				const res = await inpService.getDatsFromInp(request);
				if (isCurrentRequest) {
					setRequiredDatFiles(res ?? []);
				}
			} catch {
				if (isCurrentRequest) {
					setRequiredDatFiles([]);
				}
			}
		};
		handleGetRequiredDatFiles();

		return () => {
			isCurrentRequest = false;
		};
	}, [inpFile, selectLocation, selectedEngineType, selectedModelType]);

	useEffect(() => {
		const mmsId = searchParams.get('mmsId');
		const fileName = searchParams.get('fileName');
		if (mmsId && fileName) {
			setInpFile([{ size: 0, name: addExtension(fileName), isMms: true, mmsId }]);
		}
	}, [searchParams]);

	return (
		<div style={{ padding: '0px 3rem' }}>
			<div>
				<Button
					type="text"
					shape="round"
					className="flex items-center"
					style={{ backgroundColor: '#F7F9FA', color: '#434B56' }}
					onClick={() => {
						navigate(`/upload?selectedTab=MODEL`);
					}}
				>
					<ArrowLeftOutlined rev={undefined} /> Back to Model Files
				</Button>
			</div>
			<div className="flex column pt-2">
				<span className="text-title-page">Upload Model Files</span>
				<div
					className="mt-2 flex items-center"
					style={{
						background: 'rgba(255, 238, 203, 0.5)',
						height: '40px',
						borderLeft: 'solid 8px #FBC261',
					}}
				>
					<span className="ml-2" style={{ color: '#023E65' }}>
						{selectedEngineType === `ICM`
							? `Only INP and .icmt files accepted`
							: `Only INP files accepted`}
					</span>
				</div>
				<div className="tableContainer mt-3 flex column w-full">
					<table
						style={{
							border: '2px solid #E6E6E6',
							height: 'calc(100vh - 340px)',
							borderRadius: '10px',
							borderCollapse: 'separate',
						}}
					>
						<thead>
							<tr style={{ height: '50px' }}>
								<th
									style={{
										paddingLeft: '20px',
										borderBottom: '2px solid #E6E6E6',
									}}
								>
									File Name
								</th>
								<th
									style={{
										borderBottom: '2px solid #E6E6E6',
									}}
								>
									Size
								</th>
								<th
									style={{
										borderBottom: '2px solid #E6E6E6',
									}}
								>
									Location
								</th>
								<th
									style={{
										borderBottom: '2px solid #E6E6E6',
									}}
								>
									Model Type
								</th>
								<th
									style={{
										borderBottom: '2px solid #E6E6E6',
									}}
								>
									Engine Type
								</th>
								<th
									style={{
										borderBottom: '2px solid #E6E6E6',
									}}
								/>
							</tr>
						</thead>
						{inpFile.length === 0 ? (
							<tbody>
								<tr>
									<td colSpan={5}>
										<Dragger {...initialUploadProps} style={{ backgroundColor: 'white' }}>
											<div className="flex column" style={{}}>
												<span className="mb-2" style={{ color: '#74788D' }}>
													Drag and drop your files here or click in
												</span>
												{/* <Upload {...uploadProps}> */}
												<Button
													icon={<PlusCircleOutlined rev={undefined} />}
													shape="round"
													className="flex items-center self-center"
													style={{
														color: '#023E65',
														borderColor: '#023E65',
														backgroundColor: 'transparent',
													}}
												>
													Add Files
												</Button>
												{/* </Upload> */}
											</div>
										</Dragger>
									</td>
								</tr>
							</tbody>
						) : (
							<tbody>
								{inpFile.length > 0 && (
									<tr style={{ height: '40px' }}>
										<td style={{ paddingLeft: '20px' }}>{inpFile[0].name}</td>
										<td>{fileSize(inpFile[0].size)}</td>
										<td className="pr-2">
											<Locations
												locations={locations}
												handleClickSelect={handleClickLocation}
												defaultValue={false}
											/>
										</td>
										<td className="pr-2">
											<Select
												value={selectedModelType}
												style={{ width: '100%', borderRadius: '8px' }}
												disabled={isModelTypeLocked}
												onChange={(val) => {
													setSelectedModelType(val);
													setSelectedEngineType(val === 'WATER' ? 'EPANET' : 'SWMM');
												}}
												options={[
													{ value: 'WATER', label: 'WATER' },
													{ value: 'DRAINAGE', label: 'DRAINAGE' },
												]}
											/>
										</td>
										<td className="pr-2">
											<Select
												defaultValue={selectedEngineType}
												value={selectedEngineType}
												style={{ width: '100%', borderRadius: '8px' }}
												onChange={(val) => {
													handleSelectEngineType(val);
												}}
												options={engineOptions}
											/>
										</td>
										<td>
											<DeleteOutlined
												style={{
													cursor: 'pointer',
													fontSize: '1.3rem',
												}}
												onClick={() => {
													deleteInp();
												}}
												rev={undefined}
											/>
										</td>
									</tr>
								)}
								{requiredDatFiles.map((datName) => {
									return (
										<tr
											style={{
												height: '40px',
												backgroundColor: '#F7F9FC',
											}}
											key={datName}
										>
											<td style={{ paddingLeft: '50px' }}>{datName}</td>
											<td />
											<td>
												<Select
													mode="multiple"
													allowClear
													options={pluviometerOptions}
													placeholder={'Select'}
													value={getSelectedPluviometers(datName)}
													style={{
														width: '100%',
														maxWidth: '600px',
													}}
													onChange={(value) => {
														handleSelectPluviometers(datName, value);
													}}
												/>
											</td>
											<td />
											<td>
												<DeleteOutlined
													style={{
														cursor: 'pointer',
														fontSize: '1.3rem',
													}}
													onClick={() => {
														deleteRequiredDat(datName);
													}}
													rev={undefined}
												/>
											</td>
										</tr>
									);
								})}

								{icmtFile.length > 0 && (
									<tr style={{ height: '40px' }}>
										<td style={{ paddingLeft: '20px' }}>{icmtFile[0].name}</td>
										<td>{fileSize(icmtFile[0].size)}</td>
										<td className="pr-2" />
										<td className="pr-2" />
										<td className="pr-2" />
										<td>
											<DeleteOutlined
												style={{
													cursor: 'pointer',
													fontSize: '1.3rem',
												}}
												onClick={() => {
													deleteIcmt();
												}}
												rev={undefined}
											/>
										</td>
									</tr>
								)}

								<tr>
									<td colSpan={7}>
										<Dragger {...initialUploadProps} style={{ backgroundColor: 'white' }}>
											<Button
												icon={
													<PlusCircleOutlined
														style={{
															fontSize: '1.4rem',
														}}
														rev={undefined}
													/>
												}
												shape="round"
												style={{
													width: '50px',
													height: '50px',
													color: '#023E65',
													border: '2px solid #023E65',
													bottom: '10px',
													right: '10px',
												}}
												className="flex items-center justify-center absolute"
											/>
										</Dragger>
									</td>
								</tr>
							</tbody>
						)}
					</table>
				</div>
				<div className="flex pt-3 justify-between">
					<Button
						style={{ width: '85px', height: '40px' }}
						onClick={() => {
							navigate(`/upload?selectedTab=MODEL`);
						}}
					>
						Cancel
					</Button>
					<div>
						{/* <Button
              className="mr-2"
              disabled={inpFile.length < 0}
              style={{
                width: "100px",
                height: "40px",
                backgroundColor: "#00203C",
                color: "white",
              }}
              onClick={() => {
                goToEdditFile();
              }}
            >
              Edit Model
            </Button> */}
						<Button
							disabled={isUploadDisabled}
							style={{
								width: '120px',
								height: '40px',
								...(isUploadDisabled ? {} : { backgroundColor: '#00203C', color: 'white' }),
							}}
							onClick={() => {
								uploadFiles();
							}}
						>
							Upload Model
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}

function mapState(state: { locations: { array: any } }) {
	return {
		locations: state.locations.array,
	};
}

export default connect(mapState)(UploadModelFiles);
